/**
 * Capturing the player's own voice as a relayable fragment.
 *
 * Separate from speech recognition on purpose. The recogniser gives us *text*; the relay needs
 * *audio*, and the two come from different browser APIs that cannot share a stream cleanly. So a
 * transmission opens a MediaRecorder alongside the recogniser and stops it at the same moment.
 *
 * The recording exists as a Blob for as long as it takes to base64 it and hand it to the socket.
 * It is never stored, never written to IndexedDB, and never retained after the offer — matching
 * what the relay does at the other end and what the privacy policy tells players.
 */
import { MAX_FRAGMENT_BYTES } from './limits.js';

/** Codecs worth trying, best first. Opus in WebM is small and widely supported. */
const PREFERRED_MIME = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
];

function pickMime(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  return PREFERRED_MIME.find((mime) => MediaRecorder.isTypeSupported(mime)) ?? null;
}

export function isCaptureSupported(): boolean {
  return pickMime() !== null && typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices);
}

export interface CapturedFragment {
  audio: string;
  mime: string;
  durationMs: number;
}

/**
 * One recording session, started when the player keys up and stopped when they release.
 *
 * Every failure resolves to null rather than throwing: no microphone permission, no
 * MediaRecorder, a codec the browser will not produce — all of them mean "no fragment to relay",
 * which is a normal outcome, not an error the player should see.
 */
export class FragmentRecorder {
  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private startedAt = 0;

  async start(): Promise<boolean> {
    const mime = pickMime();
    if (!mime || !navigator.mediaDevices) return false;

    try {
      // A separate getUserMedia call from the recogniser's. Browsers share the underlying device,
      // and the permission prompt has already been answered by the time anyone transmits.
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      return false;
    }

    try {
      this.recorder = new MediaRecorder(this.stream, {
        mimeType: mime,
        audioBitsPerSecond: 24_000,
      });
    } catch {
      this.releaseStream();
      return false;
    }

    this.chunks = [];
    this.recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data.size > 0) this.chunks.push(event.data);
    };
    this.startedAt = Date.now();
    this.recorder.start();
    return true;
  }

  /** Stop and return the fragment, or null if there is nothing worth relaying. */
  async stop(): Promise<CapturedFragment | null> {
    const recorder = this.recorder;
    if (!recorder) return null;

    const mime = recorder.mimeType;
    const durationMs = Date.now() - this.startedAt;

    const blob = await new Promise<Blob | null>((resolve) => {
      recorder.onstop = () => resolve(new Blob(this.chunks, { type: mime }));
      try {
        recorder.stop();
      } catch {
        resolve(null);
      }
    });

    this.releaseStream();
    this.recorder = null;
    this.chunks = [];

    if (!blob || blob.size === 0) return null;
    // Oversized fragments are dropped here rather than rejected at the relay, so a long
    // transmission costs nothing on the wire.
    if (blob.size > MAX_FRAGMENT_BYTES) return null;

    const audio = await blobToBase64(blob);
    // The blob and its chunks fall out of scope here. Nothing else holds a reference.
    return audio ? { audio, mime, durationMs } : null;
  }

  /** Abandon the recording without producing anything. */
  cancel(): void {
    try {
      this.recorder?.stop();
    } catch {
      /* already stopped */
    }
    this.recorder = null;
    this.chunks = [];
    this.releaseStream();
  }

  private releaseStream(): void {
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    this.stream = null;
  }
}

async function blobToBase64(blob: Blob): Promise<string | null> {
  try {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    let binary = '';
    // Chunked to avoid blowing the argument limit on a large fragment.
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
      binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
    }
    return btoa(binary);
  } catch {
    return null;
  }
}
